import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { cartAPI } from '../lib/api';
import { useAuth } from './AuthContext';
import { trackAddToCart } from '../lib/tiktokPixel';
import { trackAttributionEvent, trackServerEvent, newEventId } from '../lib/attribution';
import logger from '../lib/logger';

const CartContext = createContext(null);

// Guest cart helper functions
const getGuestCart = () => {
  try {
    const cart = localStorage.getItem('guestCart');
    return cart ? JSON.parse(cart) : { items: [], total: 0, item_count: 0 };
  } catch {
    return { items: [], total: 0, item_count: 0 };
  }
};

const saveGuestCart = (cart) => {
  localStorage.setItem('guestCart', JSON.stringify(cart));
};

const calculateCartTotals = (items) => {
  const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const item_count = items.reduce((sum, item) => sum + item.quantity, 0);
  return { items, total, item_count };
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!user) {
      // Load guest cart from localStorage
      setCart(getGuestCart());
      return;
    }
    
    try {
      setLoading(true);
      const res = await cartAPI.get();
      setCart(res.data);
    } catch (error) {
      logger.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = useCallback(async (productId, quantity = 1, productData = null, size = null, color = null, customPhotoUrl = null) => {
    const upcharge = { '2XL': 4, 'XXL': 4, '3XL': 6, 'XXXL': 6, '4XL': 8, '5XL': 10 }[(size || '').toUpperCase()] || 0;
    trackAttributionEvent('add_to_cart', productId, productData?.price ?? null);
    if (productData?.price != null) {
      const eventId = newEventId('atc');
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'AddToCart', { value: productData.price * quantity, currency: 'USD', content_ids: [productId], content_type: 'product' }, { eventID: eventId });
      }
      trackServerEvent('/tracking/add-to-cart', {
        product_id: productId, product_name: productData.name || 'Product', price: productData.price, quantity, event_id: eventId,
      });
    }
    // Guest cart - use localStorage
    if (!user) {
      const guestCart = getGuestCart();
      const existingItem = guestCart.items.find(item =>
        item.product_id === productId && (item.size || null) === size && (item.color || null) === color
        && (item.custom_photo_url || null) === (customPhotoUrl || null));
      
      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        guestCart.items.push({
          ...productData,
          product_id: productId,
          quantity,
          name: productData?.name || 'Product',
          image: productData?.images?.[0] || '',
          size,
          color,
          price: (productData?.price || 0) + upcharge
        });
      }
      
      const updatedCart = calculateCartTotals(guestCart.items);
      saveGuestCart(updatedCart);
      setCart(updatedCart);
      
      // Track TikTok Pixel event
      if (productData) {
        trackAddToCart(productData, quantity);
      }
      return;
    }
    
    try {
      setLoading(true);
      const res = await cartAPI.add(productId, quantity, size, color, customPhotoUrl);
      setCart(res.data);
      
      // Track TikTok Pixel event
      if (productData) {
        trackAddToCart(productData, quantity);
        
        // Snapchat Pixel Add to Cart
        if (typeof window.snaptr === 'function') {
          window.snaptr('track', 'ADD_CART', {
            'currency': 'USD',
            'price': productData.price * quantity,
            'item_ids': [productId]
          });
        }
        
        // Meta Pixel Add to Cart
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'AddToCart', {
            content_ids: [productId],
            content_type: 'product',
            value: productData.price * quantity,
            currency: 'USD'
          });
        }
      }
      
      return res.data;
    } finally {
      setLoading(false);
    }
  }, [user]);

  const updateQuantity = useCallback(async (itemId, quantity) => {
    // Guest cart
    if (!user) {
      const guestCart = getGuestCart();
      const item = guestCart.items.find(item => item.product_id === itemId);
      if (item) {
        item.quantity = quantity;
        const updatedCart = calculateCartTotals(guestCart.items);
        saveGuestCart(updatedCart);
        setCart(updatedCart);
      }
      return;
    }
    
    try {
      setLoading(true);
      const res = await cartAPI.update(itemId, quantity);
      setCart(res.data);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, [user]);

  const removeItem = useCallback(async (itemId) => {
    // Guest cart
    if (!user) {
      const guestCart = getGuestCart();
      guestCart.items = guestCart.items.filter(item => item.product_id !== itemId);
      const updatedCart = calculateCartTotals(guestCart.items);
      saveGuestCart(updatedCart);
      setCart(updatedCart);
      return;
    }
    
    try {
      setLoading(true);
      const res = await cartAPI.remove(itemId);
      setCart(res.data);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, [user]);

  const clearCart = useCallback(async () => {
    // Guest cart
    if (!user) {
      const emptyCart = { items: [], total: 0, item_count: 0 };
      saveGuestCart(emptyCart);
      setCart(emptyCart);
      return;
    }
    
    try {
      setLoading(true);
      await cartAPI.clear();
      setCart({ items: [], total: 0, item_count: 0 });
    } finally {
      setLoading(false);
    }
  }, [user]);

  const value = useMemo(() => ({
    cart,
    loading,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    refreshCart: fetchCart,
  }), [cart, loading, addToCart, updateQuantity, removeItem, clearCart, fetchCart]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
